import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  DataTable,
  EmptyState,
  Spinner,
  Banner,
  TextField,
  Button,
  ButtonGroup,
  Box,
  InlineStack,
  BlockStack,
  Grid,
  Collapsible,
  Icon,
  Select,
} from '@shopify/polaris';
import { CalendarIcon, FilterIcon } from '@shopify/polaris-icons';
import katanaApi, { KatanaApiError } from '@/services/katana';
import type { DashboardMetrics, PurchaseOrder, Supplier, InventoryMetrics, Location } from '@/types/katana';
import '../../katana-colors.css';

/**
 * Katana Brand Color Palette Implementation
 * 
 * This dashboard uses Katana's official brand colors to create a cohesive visual experience:
 * 
 * PRIMARY COLORS:
 * - Spiked Yellow (#EFFF38): Brand highlight, accent elements
 * - Deep Green (#092332): Primary brand color, headers, primary actions
 * - Great White (#FFFFFF): Background, text on dark surfaces
 * 
 * BRAG STATUS SYSTEM (Blue, Red, Amber, Green):
 * - BRAG Blue (#003E9B): Information, neutral status
 * - BRAG Red (#F42C00): Critical alerts, errors, overdue items
 * - BRAG Amber (#FFD66C): Warnings, partial status, caution
 * - BRAG Green (#348558): Success, completed items, positive status
 * 
 * SUPPORTING COLORS:
 * - Vivid Green (#06BC87): Active inventory, movement indicators
 * - Bright Red (#FB4F43): Urgent alerts, critical status
 * - Rich Gray (#C9D7D4): Borders, subtle backgrounds
 * - Off White (#F7F9F8): Card backgrounds, neutral surfaces
 * 
 * Usage Guidelines:
 * - Use BRAG colors for status indicators and badges
 * - Apply Deep Green for primary actions and headers
 * - Use Spiked Yellow sparingly for key highlights
 * - Maintain accessibility with sufficient contrast ratios
 */
const KATANA_COLORS = {
  // Primary Brand Colors
  spikedYellow: '#EFFF38',
  deepGreen: '#092332', 
  greatWhite: '#FFFFFF',
  
  // Neutral System
  offWhite: '#F7F9F8',
  paleGray: '#EFE3E2', 
  richGray: '#C9D7D4',
  
  // Status Colors
  vividGreen: '#06BC87',
  brightRed: '#FB4F43',
  faintPink: '#FFCECD',
  
  // BRAG Status System
  bragBlue: '#003E9B',
  bragRed: '#F42C00',
  bragAmber: '#FFD66C',
  bragGreen: '#348558'
};

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [suppliers, setSuppliers] = useState<Record<number, string>>({});
  const [inventoryMetrics, setInventoryMetrics] = useState<InventoryMetrics | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [primaryLocation, setPrimaryLocation] = useState<Location | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('primary');
  const [loading, setLoading] = useState(true);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Date range state
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [isFiltering, setIsFiltering] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Computed values for better UX
  const hasActiveFilters = dateFrom || dateTo;
  const filterSummary = hasActiveFilters 
    ? `${dateFrom ? new Date(dateFrom).toLocaleDateString() : 'Beginning'} - ${dateTo ? new Date(dateTo).toLocaleDateString() : 'Present'}`
    : '';

  // Helper function to retry API calls with exponential backoff
  const retryApiCall = async <T,>(
    apiCall: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000,
    callName: string = 'API call'
  ): Promise<T> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 ${callName} - Attempt ${attempt}/${maxRetries}`);
        const result = await apiCall();
        console.log(`✅ ${callName} - Success on attempt ${attempt}`);
        return result;
      } catch (error) {
        console.warn(`⚠️ ${callName} - Failed on attempt ${attempt}:`, error);
        
        if (attempt === maxRetries) {
          console.error(`❌ ${callName} - All ${maxRetries} attempts failed`);
          throw error;
        }
        
        // Exponential backoff: 1s, 2s, 4s, etc.
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`⏳ ${callName} - Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw new Error(`Failed after ${maxRetries} attempts`);
  };

  useEffect(() => {
    // Prevent duplicate initialization
    if (isInitialized) {
      console.log('🔄 Dashboard already initialized, skipping...');
      return;
    }

    // Staggered initialization to prevent API rate limiting
    const initializeDashboard = async () => {
      const now = new Date();
      const fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();
      
      // Set the default date range to current month
      setDateFrom(fromDate);
      setDateTo(toDate);
      
      console.log('🚀 Starting dashboard initialization with supplier-first loading...');
      
      try {
        // Set loading state at the beginning
        setLoading(true);
        setError(null);
        
        // Step 1: Load suppliers and dashboard data together (suppliers first)
        console.log('📊 Step 1: Loading suppliers and dashboard metrics together...');
        await loadDashboardAndSuppliers(fromDate, toDate);
        
        // Step 2: Wait 1 second, then load inventory metrics
        console.log('⏳ Step 2: Waiting 1 second before loading inventory metrics...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        await retryApiCall(
          () => loadInventoryMetricsInternal(),
          3,
          1000,
          'Inventory Metrics'
        );
        
        console.log('✅ Dashboard initialization complete!');
        setIsInitialized(true);
      } catch (error) {
        console.error('❌ Failed to initialize dashboard after all retries:', error);
        setError('Failed to load dashboard data. Please try refreshing the page.');
      } finally {
        // Always set loading to false when initialization is complete
        setLoading(false);
      }
    };
    
    initializeDashboard();
  }, [isInitialized]);

  useEffect(() => {
    // Small delay to prevent rapid API calls when location changes
    const timeout = setTimeout(() => {
      retryApiCall(
        () => loadInventoryMetricsInternal(),
        2,
        500,
        'Inventory Metrics (Location Change)'
      ).catch(error => {
        console.error('❌ Failed to load inventory metrics after location change:', error);
      });
    }, 300);
    
    return () => clearTimeout(timeout);
  }, [selectedLocationId]);

  // Internal function for dashboard data loading (without state management)
  const loadDashboardDataInternal = async (fromDate: string, toDate: string) => {
    console.log('🔄 Fetching dashboard metrics...', { fromDate, toDate });
    const data = await katanaApi.getDashboardMetrics(
      fromDate || undefined, 
      toDate || undefined
    );
    console.log('✅ Dashboard metrics loaded successfully:', data);
    console.log('🔍 Detailed metrics breakdown:', {
      total_purchase_orders: data.total_purchase_orders,
      total_value: data.total_value,
      total_value_by_currency: data.total_value_by_currency,
      by_status: data.by_status,
      by_entity_type: data.by_entity_type,
      overdue_orders: data.overdue_orders,
      recent_activity_count: data.recent_activity?.length || 0,
      calculated_average: data.total_purchase_orders > 0 ? (data.total_value / data.total_purchase_orders) : 0
    });
    setMetrics(data);
    return data;
  };

  // Internal function for suppliers loading
  const loadSuppliersInternal = async () => {
    console.log('🔄 Fetching suppliers...');
    const suppliersData = await katanaApi.getSuppliers();
    console.log(`✅ Fetched ${suppliersData.length} suppliers:`, suppliersData.slice(0, 3));
    
    const supplierMap = suppliersData.reduce((acc, supplier: Supplier) => {
      acc[supplier.id] = supplier.name;
      return acc;
    }, {} as Record<number, string>);
    
    console.log('📋 Supplier mapping created:', Object.keys(supplierMap).length, 'suppliers');
    setSuppliers(supplierMap);
    return supplierMap;
  };

  // Combined data loading function to ensure suppliers are loaded first
  const loadDashboardAndSuppliers = async (fromDate: string, toDate: string) => {
    console.log('🔄 Loading suppliers and dashboard data together...');
    
    // Load suppliers first to ensure they're available for dashboard rendering
    const supplierMap = await retryApiCall(
      () => loadSuppliersInternal(),
      3,
      1000,
      'Suppliers'
    );
    
    // Small delay to prevent API overload
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Then load dashboard data
    const dashboardData = await retryApiCall(
      () => loadDashboardDataInternal(fromDate, toDate),
      3,
      1000,
      'Dashboard Metrics'
    );
    
    console.log('✅ Suppliers and dashboard data loaded successfully');
    return { supplierMap, dashboardData };
  };

  // Internal function for inventory metrics loading
  const loadInventoryMetricsInternal = async () => {
    setInventoryLoading(true);
    
    try {
      if (selectedLocationId === 'primary') {
        // Always load locations if needed to identify the primary location
        let currentLocations = locations;
        if (currentLocations.length === 0) {
          console.log('🔄 Fetching locations...');
          const locationsData = await katanaApi.getLocations();
          setLocations(locationsData);
          currentLocations = locationsData;
          
          // Add small delay after fetching locations to prevent API overload
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Find primary location from current locations (not state)
        const primary = currentLocations.find(loc => loc.is_primary) || currentLocations[0] || null;
        
        if (!primary) {
          console.warn('⚠️ No primary location found');
          return;
        }
        
        // Update primaryLocation state if it's different
        if (!primaryLocation || primaryLocation.id !== primary.id) {
          setPrimaryLocation(primary);
        }
        
        console.log(`🔄 Fetching primary location (${primary.name}) metrics...`);
        const primaryMetrics = await katanaApi.getInventoryMetricsByLocation(primary.id);
        
        console.log('🔍 Primary location API response:', {
          total_value: primaryMetrics?.total_value_in_stock,
          total_value_type: typeof primaryMetrics?.total_value_in_stock,
          is_array: Array.isArray(primaryMetrics?.total_value_in_stock),
          raw_response: primaryMetrics
        });
        
        console.log(`✅ Fetched primary location metrics: $${primaryMetrics?.total_value_in_stock?.toLocaleString() || 0}`);
        
        setInventoryMetrics(primaryMetrics);
        return primaryMetrics;
      } else {
        // Load specific location metrics only when requested
        const locationId = parseInt(selectedLocationId);
        const selectedLocation = locations.find(loc => loc.id === locationId);
        
        console.log(`🔄 Fetching location ${selectedLocation?.name || locationId} metrics...`);
        const data = await katanaApi.getInventoryMetricsByLocation(locationId);
        
        console.log('🔍 Specific location API response:', {
          locationId,
          locationName: selectedLocation?.name,
          total_value: data?.total_value_in_stock,
          total_value_type: typeof data?.total_value_in_stock,
          is_array: Array.isArray(data?.total_value_in_stock),
          raw_response: data
        });
        
        console.log(`✅ Fetched location ${locationId} metrics: $${data?.total_value_in_stock?.toLocaleString() || 0}`);
        
        setInventoryMetrics(data);
        return data;
      }
    } finally {
      setInventoryLoading(false);
    }
  };

  const loadDashboardData = async (customDateFrom?: string, customDateTo?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const fromDate = customDateFrom !== undefined ? customDateFrom : dateFrom;
      const toDate = customDateTo !== undefined ? customDateTo : dateTo;
      
      await loadDashboardAndSuppliers(fromDate, toDate);
    } catch (err) {
      console.error('❌ Failed to load dashboard data:', err);
      if (err instanceof KatanaApiError) {
        if (err.code === 'NOT_CONFIGURED') {
          setError('Katana API not configured. Please configure your Katana API credentials in the Settings page.');
        } else if (err.code === 'NETWORK_ERROR') {
          setError('Unable to connect to Katana API. Please check your API credentials and server URL in Settings.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  };

  const handleApplyDateFilter = async () => {
    setIsFiltering(true);
    await loadDashboardData();
  };

  const handleClearDateFilter = async () => {
    setDateFrom('');
    setDateTo('');
    setIsFiltering(true);
    await loadDashboardData('', '');
  };

  const handleDatePreset = async (preset: string) => {
    const now = new Date();
    let fromDate = '';
    let toDate = '';
    
    switch (preset) {
      case 'today':
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59).toISOString();
        break;
      case 'thisWeek':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        fromDate = startOfWeek.toISOString();
        toDate = endOfWeek.toISOString();
        break;
      case 'thisMonth':
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();
        break;
      case 'last30Days':
        toDate = now.toISOString();
        fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'thisYear':
        fromDate = new Date(now.getFullYear(), 0, 1).toISOString();
        toDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59).toISOString();
        break;
      default:
        return;
    }
    
    setDateFrom(fromDate);
    setDateTo(toDate);
    setIsFiltering(true);
    await loadDashboardData(fromDate, toDate);
  };

  const formatDateForInput = (isoString: string) => {
    return isoString ? isoString.split('T')[0] : '';
  };

  const formatDateFromInput = (dateString: string) => {
    return dateString ? new Date(dateString + 'T00:00:00').toISOString() : '';
  };

  const formatDateToInput = (dateString: string) => {
    return dateString ? new Date(dateString + 'T23:59:59').toISOString() : '';
  };

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'RECEIVED':
        return <Badge tone="success">Received</Badge>;
      case 'PARTIALLY_RECEIVED':
        return <Badge tone="warning">Partially Received</Badge>;
      case 'NOT_RECEIVED':
        return <Badge tone="attention">Not Received</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="600">
                <BlockStack gap="400" align="center">
                  <Spinner size="large" />
                  <Text as="p" variant="bodyLg" alignment="center">
                    Loading dashboard data...
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Banner tone="critical" title="Unable to load dashboard">
              <p>{error}</p>
              <p>
                Please check your Katana API configuration in{' '}
                <a href="/settings">Settings</a>.
              </p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!metrics) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <EmptyState
              heading="No data available"
              action={{
                content: 'Configure Katana API',
                url: '/settings',
              }}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Connect your Katana MRP account to view dashboard data.</p>
            </EmptyState>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const recentActivityRows = (metrics?.recent_activity || []).map((po: PurchaseOrder) => {
    // Debug supplier mapping for each purchase order
    const supplierName = suppliers[po.supplier_id];
    if (!supplierName) {
      console.log('🔍 Supplier mapping debug:', {
        poOrderNo: po.order_no,
        poSupplierId: po.supplier_id,
        supplierIdType: typeof po.supplier_id,
        availableSupplierIds: Object.keys(suppliers), // Show ALL supplier IDs
        suppliersCount: Object.keys(suppliers).length,
        sampleSuppliers: Object.entries(suppliers).slice(0, 5), // Show more sample mappings
        allSupplierIdsAsNumbers: Object.keys(suppliers).map(id => parseInt(id)),
        // Test both string and number lookups
        checkDirectLookup: po.supplier_id in suppliers,
        checkStringLookup: String(po.supplier_id) in suppliers,
        actualSupplierValue: suppliers[po.supplier_id as keyof typeof suppliers]
      });
    }
    
    return [
      po.order_no,
      supplierName || 'Unknown Supplier',
      getStatusBadge(po.status),
      po.entity_type === 'outsourced' ? 'Outsourced' : 'Regular',
      `$${po.total?.toFixed(2) || '0.00'}`,
      po.expected_arrival_date 
        ? new Date(po.expected_arrival_date).toLocaleDateString()
        : 'No date'
    ];
  });

  return (
    <>
      {/* Page Header with Actions */}
      <Card>
        <Box padding="400">
          <InlineStack align="space-between" wrap={false}>
            <BlockStack gap="200">
              <InlineStack gap="200" align="start" wrap={false}>
                <Text as="h1" variant="headingLg">Manufacturing Dashboard</Text>
                {hasActiveFilters && <Badge tone="info">{filterSummary}</Badge>}
              </InlineStack>
              <Text as="p" variant="bodyMd" tone="subdued">
                {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Text>
            </BlockStack>
            <Button
              icon={FilterIcon}
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              accessibilityLabel={`${filtersExpanded ? 'Hide' : 'Show'} date range filters`}
            >
              {filtersExpanded ? 'Hide filters' : 'Show filters'}
            </Button>
          </InlineStack>
        </Box>
      </Card>

      <Layout>
                {/* Hero Section - Split Card Design with Prominent Katana Logo */}
        <Layout.Section>
          <div style={{ 
            display: 'flex', 
            minHeight: '280px',
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Left Side - Katana Logo Section */}
            <div style={{ 
              flex: '0 0 45%',
              backgroundColor: '#F7F9F8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}>
              <img
                src="https://katanamrp.com/wp-content/uploads/2021/06/katana-logo-1920x1080-1-1300x731.png"
                alt="Katana MRP Logo"
                style={{ 
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '240px',
                  objectFit: 'contain'
                }}
              />
            </div>
            
            {/* Right Side - Content Section */}
            <div style={{ flex: '1', padding: '0' }}>
              <Box padding="600">
                  <BlockStack gap="500">
                    {/* Enhanced Typography Hierarchy */}
                    <BlockStack gap="300">
                      <Text as="h1" variant="headingXl" tone="base">
                        Total Inventory Value
                      </Text>
                      <InlineStack gap="200" wrap={false}>
                        <Text as="p" variant="bodyLg" tone="subdued">
                          {selectedLocationId === 'primary' && primaryLocation
                            ? `${primaryLocation.name} (Primary Location)`
                            : locations.find(loc => loc.id.toString() === selectedLocationId)?.name || 'All Locations'
                          }
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          • {new Date().toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </Text>
                      </InlineStack>
                    </BlockStack>
                    
                    {/* Enhanced Display Metric with Proper Scoping */}
                    <BlockStack gap="300">
                      {inventoryLoading ? (
                        <Box>
                          <InlineStack gap="200" align="center">
                            <Spinner size="small" />
                            <Text as="p" variant="heading3xl" tone="subdued">
                              Loading...
                            </Text>
                          </InlineStack>
                        </Box>
                      ) : (
                        <InlineStack gap="400" align="start" wrap={false}>
                          <Text as="p" variant="heading3xl" tone="success">
                            {inventoryMetrics 
                              ? (typeof inventoryMetrics.total_value_in_stock === 'number' && !isNaN(inventoryMetrics.total_value_in_stock)
                                  ? formatCurrency(inventoryMetrics.total_value_in_stock)
                                  : '$0.00')
                              : '$0.00'
                            }
                          </Text>
                          {/* Movement indicator with Katana brand colors */}
                          {inventoryMetrics && inventoryMetrics.total_value_in_stock > 0 && (
                            <Box paddingBlockStart="200">
                              <div style={{ 
                                backgroundColor: KATANA_COLORS.vividGreen, 
                                color: KATANA_COLORS.greatWhite, 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'inline-block'
                              }}>
                                ↗ Active inventory
                              </div>
                            </Box>
                          )}
                        </InlineStack>
                      )}
                    </BlockStack>

                    {/* Enhanced Location Selector */}
                    {locations.length > 0 && (
                      <Box maxWidth="350px">
                        <Select
                          label="Location"
                          options={[
                            { 
                              label: primaryLocation ? `${primaryLocation.name} (Primary)` : 'Primary Location', 
                              value: 'primary' 
                            },
                            ...locations
                              .filter(location => !location.is_primary)
                              .map(location => ({
                                label: location.name,
                                value: location.id.toString()
                              }))
                          ]}
                          value={selectedLocationId}
                          onChange={setSelectedLocationId}
                        />
                      </Box>
                    )}
                  </BlockStack>
                </Box>
              </div>
            </div>
        </Layout.Section>

        {/* Date Range Filters */}
        <Layout.Section>
          <Collapsible
            open={filtersExpanded}
            id="date-filters"
            transition={{duration: '200ms', timingFunction: 'ease-in-out'}}
          >
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  <InlineStack gap="200" align="start">
                    <Icon source={CalendarIcon} />
                    <Text as="h2" variant="headingSm">
                      Date Range Filters
                    </Text>
                  </InlineStack>
                  
                  <Grid>
                    <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd">
                          Quick Filters
                        </Text>
                        <ButtonGroup variant="segmented">
                          <Button 
                            size="slim" 
                            onClick={() => handleDatePreset('thisMonth')}
                            loading={isFiltering}
                          >
                            This Month
                          </Button>
                          <Button 
                            size="slim" 
                            onClick={() => handleDatePreset('last30Days')}
                            loading={isFiltering}
                          >
                            Last 30 Days
                          </Button>
                          <Button 
                            size="slim" 
                            onClick={() => handleDatePreset('thisYear')}
                            loading={isFiltering}
                          >
                            This Year
                          </Button>
                        </ButtonGroup>
                      </BlockStack>
                    </Grid.Cell>
                    
                    <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                      <BlockStack gap="200">
                        <Text as="p" variant="bodyMd">
                          Custom Range
                        </Text>
                        <InlineStack gap="200" wrap={false}>
                          <TextField
                            label="From"
                            labelHidden
                            type="date"
                            value={formatDateForInput(dateFrom)}
                            onChange={(value) => setDateFrom(formatDateFromInput(value))}
                            autoComplete="off"
                            placeholder="From date"
                          />
                          <TextField
                            label="To"
                            labelHidden
                            type="date"
                            value={formatDateForInput(dateTo)}
                            onChange={(value) => setDateTo(formatDateToInput(value))}
                            autoComplete="off"
                            placeholder="To date"
                          />
                          <Button 
                            variant="primary" 
                            onClick={handleApplyDateFilter}
                            loading={isFiltering}
                            size="slim"
                          >
                            Apply
                          </Button>
                          <Button 
                            onClick={handleClearDateFilter}
                            loading={isFiltering}
                            size="slim"
                          >
                            Clear
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Grid.Cell>
                  </Grid>

                  {metrics.filters_applied && (
                    <Box background="bg-surface-secondary" padding="200" borderRadius="200">
                      <Text as="p" variant="bodySm" tone="subdued">
                        Showing {metrics.filters_applied.total_records_fetched} records
                        {hasActiveFilters && ` for ${filterSummary.toLowerCase()}`}
                      </Text>
                    </Box>
                  )}
                </BlockStack>
              </Box>
            </Card>
          </Collapsible>
        </Layout.Section>

        {/* Loading indicator */}
        {isFiltering && (
          <Layout.Section>
            <Card>
              <Box padding="300">
                <InlineStack gap="200" align="center">
                  <Spinner size="small" />
                  <Text as="p" variant="bodyMd">
                    Updating dashboard data...
                  </Text>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Key Metrics Following Polaris Card Layout Patterns */}
        <Layout.Section>
          <BlockStack gap="500">
            <Text as="h2" variant="headingLg">
              Key Purchase Order Metrics
            </Text>
            <Grid>
              {/* Purchase Orders Metric - Proper Card Anatomy */}
              <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                <Card>
                  <Box padding="400">
                    <BlockStack gap="200">
                      {/* Card Header */}
                      <Text as="h3" variant="headingSm" tone="subdued">
                        Purchase Orders
                      </Text>
                      
                      {/* Card Body - Main Metric */}
                      <BlockStack gap="100" align="center">
                        <Text as="p" variant="heading2xl" tone="base" alignment="center">
                          {metrics.total_purchase_orders.toLocaleString()}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                          this period
                        </Text>
                      </BlockStack>
                      
                      {/* Card Footer - Supporting Info */}
                      <InlineStack align="center">
                        <Badge tone="info">Total orders</Badge>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Grid.Cell>

              {/* Total Value Metric */}
              <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                <Card>
                  <Box padding="400">
                    <BlockStack gap="200">
                      {/* Card Header */}
                      <Text as="h3" variant="headingSm" tone="subdued">
                        Total Order Value
                      </Text>
                      
                      {/* Card Body - Main Metric */}
                      <BlockStack gap="100" align="center">
                        <Text 
                          as="p" 
                          variant={formatCurrency(metrics.total_value).length > 12 ? "headingLg" : "heading2xl"} 
                          tone="success" 
                          alignment="center"
                        >
                          {formatCurrency(metrics.total_value)}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                          across all orders
                        </Text>
                      </BlockStack>
                      
                      {/* Card Footer - Supporting Info */}
                      <InlineStack align="center">
                        <Badge tone="success">
                          {metrics.total_purchase_orders > 0 
                            ? `${formatCurrency(metrics.total_value / metrics.total_purchase_orders)} avg`
                            : '$0.00 avg'
                          }
                        </Badge>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Grid.Cell>
 
              {/* Overdue Orders Metric */}
              <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                <Card>
                  <Box padding="400">
                    <BlockStack gap="200">
                      {/* Card Header */}
                      <Text as="h3" variant="headingSm" tone="subdued">
                        Overdue Orders
                      </Text>
                      
                      {/* Card Body - Main Metric */}
                      <BlockStack gap="100" align="center">
                        <Text 
                          as="p" 
                          variant="heading2xl" 
                          tone={metrics.overdue_orders > 0 ? 'critical' : 'success'} 
                          alignment="center"
                        >
                          {metrics.overdue_orders}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                          {metrics.overdue_orders > 0 ? 'need attention' : 'all on time'}
                        </Text>
                      </BlockStack>
                      
                      {/* Card Footer - Supporting Info */}
                      <InlineStack align="center">
                        <Badge tone={metrics.overdue_orders === 0 ? 'success' : 'critical'}>
                          {metrics.overdue_orders === 0 
                            ? '100% on time' 
                            : `${Math.round((metrics.overdue_orders / metrics.total_purchase_orders) * 100)}% overdue`
                          }
                        </Badge>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Grid.Cell>
 
              {/* Fulfillment Rate Metric */}
              <Grid.Cell columnSpan={{xs: 6, sm: 3, md: 3, lg: 3, xl: 3}}>
                <Card>
                  <Box padding="400">
                    <BlockStack gap="200">
                      {/* Card Header */}
                      <Text as="h3" variant="headingSm" tone="subdued">
                        Fulfillment Rate
                      </Text>
                      
                      {/* Card Body - Main Metric */}
                      <BlockStack gap="100" align="center">
                        <Text as="p" variant="heading2xl" tone="success" alignment="center">
                          {Math.round(((metrics.by_status.RECEIVED || 0) / metrics.total_purchase_orders) * 100)}%
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                          orders completed
                        </Text>
                      </BlockStack>
                      
                      {/* Card Footer - Supporting Info */}
                      <InlineStack align="center">
                        <Badge tone="success">
                          {`${(metrics.by_status.RECEIVED || 0)} received`}
                        </Badge>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Grid.Cell>
            </Grid>
          </BlockStack>
        </Layout.Section>

        {/* Status Breakdown - Mobile-Responsive Layout */}
        <Layout.Section>
          <BlockStack gap="400">
            <Text as="h2" variant="headingLg">
              Order Breakdown
            </Text>
            <Grid>
              {/* Order Status Card */}
              <Grid.Cell columnSpan={{xs: 6, sm: 6, md: 6, lg: 4, xl: 4}}>
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">
                        Order Status
                      </Text>
                      <BlockStack gap="200">
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd">Received</Text>
                          <Badge tone="success">{(metrics.by_status.RECEIVED || 0).toString()}</Badge>
                        </InlineStack>
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd">Partially Received</Text>
                          <Badge tone="warning">{(metrics.by_status.PARTIALLY_RECEIVED || 0).toString()}</Badge>
                        </InlineStack>
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd">Not Received</Text>
                          <Badge tone="attention">{(metrics.by_status.NOT_RECEIVED || 0).toString()}</Badge>
                        </InlineStack>
                      </BlockStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Grid.Cell>

              {/* Order Type Card */}
              <Grid.Cell columnSpan={{xs: 6, sm: 6, md: 6, lg: 4, xl: 4}}>
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">
                        Order Type
                      </Text>
                      <BlockStack gap="200">
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd">Regular Orders</Text>
                          <Badge>{(metrics.by_entity_type.regular || 0).toString()}</Badge>
                        </InlineStack>
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd">Outsourced Orders</Text>
                          <Badge>{(metrics.by_entity_type.outsourced || 0).toString()}</Badge>
                        </InlineStack>
                      </BlockStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Grid.Cell>

              {/* Performance Summary Card */}
              <Grid.Cell columnSpan={{xs: 6, sm: 6, md: 6, lg: 4, xl: 4}}>
                <Card>
                  <Box padding="400">
                    <BlockStack gap="300">
                      <Text as="h3" variant="headingSm">
                        Performance
                      </Text>
                      <BlockStack gap="200">
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd">On-Time Delivery</Text>
                          <Badge tone={metrics.overdue_orders === 0 ? 'success' : 'attention'}>
                            {metrics.overdue_orders === 0 ? '100%' : `${Math.round((1 - metrics.overdue_orders / metrics.total_purchase_orders) * 100)}%`}
                          </Badge>
                        </InlineStack>
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodyMd">Avg. Order Value</Text>
                          <Badge>
                            {metrics.total_purchase_orders > 0
                              ? formatCurrency(metrics.total_value / metrics.total_purchase_orders)
                              : '$0.00'
                            }
                          </Badge>
                        </InlineStack>
                      </BlockStack>
                    </BlockStack>
                  </Box>
                </Card>
              </Grid.Cell>
            </Grid>
          </BlockStack>
        </Layout.Section>

        {/* Recent Activity Table */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text as="h2" variant="headingLg">
                  Recent Purchase Orders
                </Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text']}
                  headings={[
                    'Order #',
                    'Supplier',
                    'Status',
                    'Type',
                    'Total',
                    'Expected Arrival',
                  ]}
                  rows={recentActivityRows}
                  footerContent={
                    recentActivityRows.length > 0 
                      ? `Showing ${recentActivityRows.length} of ${metrics.total_purchase_orders} orders`
                      : undefined
                  }
                />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </>
  );
}
